import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import Hero from '../../components/marketing/Hero';
import CtaBanner from '../../components/marketing/CtaBanner';
import Link from '../../components/Link';
import s from './Blog.css';

// Blog / Guides hub — ADDITIVE "coming soon" index.
// HONESTY: there are NO published articles yet. These cards are PLANNED/upcoming topics, clearly marked
// "Coming soon". Do NOT link to article pages that don't exist, and do NOT publish fake full articles.
// Thin-content-safe: every teaser is labelled and links nowhere (no dead /blog/:slug routes).
// LEGAL_REVIEW_REQUIRED: any future guide that states pricing, legal or safety guidance must be reviewed
// before publishing.

// Planned evergreen topics (teasers only — not yet written).
const TOPICS = [
  {
    icon: 'M9 11l3 3 8-8M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
    title: 'How to prep for a handyman visit',
    teaser: 'A quick checklist to get your space and project ready so the job goes smoothly the first time.',
    tag: 'Getting started',
  },
  {
    icon: 'M2 10h20M6 15h4m8-9H6a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4V10a4 4 0 0 0-4-4Z',
    title: 'What does TV mounting cost?',
    teaser: 'What goes into a TV-mounting quote — wall type, mount, cable management — and how to budget for it.',
    tag: 'Pricing',
  },
  {
    icon: 'M12 2 4 6v6c0 5 3.4 8 8 10 4.6-2 8-5 8-10V6l-8-4ZM9 12l2 2 4-4',
    title: 'Hiring a vetted pro: a checklist',
    teaser: 'What “vetted” really means, the questions worth asking, and how to spot a great local pro.',
    tag: 'Trust & safety',
  },
  {
    icon: 'M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6',
    title: 'Small home repairs worth not putting off',
    teaser: 'The little fixes that get more expensive the longer you wait — and roughly what they take.',
    tag: 'Home tips',
  },
  {
    icon: 'M20 7 9 18l-5-5M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0',
    title: 'Moving help, made less stressful',
    teaser: 'How to scope a move, what to have ready, and when extra hands actually save you money.',
    tag: 'Moving',
  },
  {
    icon: 'M3 3v18h18M7 14l4-4 3 3 5-6',
    title: 'Understanding your estimate vs. final bill',
    teaser: 'Why a final bill can differ from an estimate — materials, time, extra workers — explained plainly.',
    tag: 'Pricing',
  },
];

// JSON-LD: a Blog node only, with NO blogPost entries — we deliberately do NOT mark up articles that
// don't exist (avoids implying published content to search engines / FTC issues).
const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: 'BestHand Guides & Tips',
  description: 'Practical guides for hiring trusted local pros. Coming soon.',
  publisher: { '@type': 'Organization', name: 'BestHand' },
};

function Blog() {
  return (
    <main className={s.page}>
      {/* SECURITY: escape </script> breakouts in JSON-LD (defense-in-depth). */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026') }}
      />

      <Hero
        eyebrow="Guides & Tips · Coming soon"
        title="Practical guides for getting it done."
        subtitle="We’re writing honest, useful guides on prepping for a visit, what jobs cost, and hiring a vetted pro. They’re on the way — here’s a peek at what’s coming."
        badges={['Coming soon', 'Honest & practical', 'No fluff']}
      />

      {/* Coming-soon banner */}
      <section className={s.soonSec}>
        <div className={s.wrap}>
          <div className={s.soonBadge}>
            <span className={s.soonDot} aria-hidden="true" />
            Coming soon
          </div>
          <p className={s.soonText}>
            Our guides aren’t published yet. The topics below are what we’re working on first — want to know
            when they’re live? <Link to="/contact-us" className={s.inlineLink}>Get notified</Link>.
          </p>
        </div>
      </section>

      {/* Planned topics */}
      <section className={`${s.sec} ${s.alt}`}>
        <div className={s.wrap}>
          <h2 className={s.h2}>What we’re working on</h2>
          <div className={s.grid}>
            {TOPICS.map((t) => (
              // Not a link — these articles don't exist yet. Marked "Coming soon".
              <article className={s.card} key={t.title} aria-disabled="true">
                <span className={s.cardIc}>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d={t.icon} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className={s.cardTag}>{t.tag}</span>
                <h3 className={s.cardTitle}>{t.title}</h3>
                <p className={s.cardText}>{t.teaser}</p>
                <span className={s.cardSoon}>Coming soon</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Get notified */}
      <section className={s.sec}>
        <div className={s.wrap}>
          <div className={s.notifyCard}>
            <div>
              <h2 className={s.h2Left}>Get notified when guides go live</h2>
              <p className={s.notifyText}>
                We’ll let you know when the first guides are published — no spam, just useful reads.
              </p>
            </div>
            <div className={s.notifyActions}>
              <Link to="/contact-us" className={s.notifyBtn}>Get notified</Link>
              <a href="mailto:support@besthandapp.com" className={s.notifyBtnAlt}>support@besthandapp.com</a>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner
        title="Need a hand today?"
        subtitle="Book a trusted local pro with upfront, industry-low pricing."
        primaryLabel="Browse services"
        primaryTo="/services"
        secondaryLabel="How it works"
        secondaryTo="/how-it-works"
      />
    </main>
  );
}

export default withStyles(s)(Blog);
